import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ClipboardList, UserPlus, FileCheck, CheckCircle, Clock, AlertTriangle, Upload, Shield, Settings, ChevronRight, X, Plus, Trash2 } from 'lucide-react';
import { isAdmin, isHR } from '@/lib/auth';
import api from '@/lib/api';

const STATUS_STYLES = {
  pending: 'bg-slate-100 text-slate-600',
  completed: 'bg-green-100 text-green-700',
  uploaded: 'bg-blue-100 text-blue-700',
  verified: 'bg-green-100 text-green-700',
  in_progress: 'bg-amber-100 text-amber-700',
};

// --- Admin: Status Tracker ---
function OnboardingTracker() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDialog, setStartDialog] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [starting, setStarting] = useState(false);
  const [detailOb, setDetailOb] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const { data } = await api.get('/api/onboarding/all');
      setItems(data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openStartDialog = async () => {
    try {
      const { data } = await api.get('/api/employees');
      const existingIds = items.map(i => i.employee_id);
      setEmployees(data.filter(e => !existingIds.includes(e.id) && e.status === 'active'));
    } catch { /* ignore */ }
    setStartDialog(true);
  };

  const handleStart = async () => {
    if (!selectedEmp) { toast.error('Select an employee'); return; }
    setStarting(true);
    try {
      await api.post('/api/onboarding/start', { employee_id: selectedEmp });
      toast.success('Onboarding started');
      setStartDialog(false);
      setSelectedEmp('');
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setStarting(false);
  };

  const handleComplete = async (id) => {
    try {
      await api.put(`/api/onboarding/${id}/complete`);
      toast.success('Onboarding completed');
      fetch();
      setDetailOb(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleVerifyDoc = async (obId, docId) => {
    try {
      await api.put(`/api/onboarding/${obId}/document/${docId}/verify`);
      toast.success('Document verified');
      const { data } = await api.get(`/api/onboarding/${obId}`);
      setDetailOb(data);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{items.filter(i => i.status === 'in_progress').length} in progress, {items.filter(i => i.status === 'completed').length} completed</p>
        <Button onClick={openStartDialog} className="bg-[#0D2850] hover:bg-[#0B2246] gap-2 text-sm" data-testid="start-onboarding-btn">
          <UserPlus className="w-4 h-4" /> Start Onboarding
        </Button>
      </div>

      {loading ? <p className="text-center text-slate-400 py-8">Loading...</p> : (
        <div className="space-y-3">
          {items.map(ob => (
            <Card key={ob.id} className="border-slate-200 cursor-pointer hover:shadow-sm transition-shadow" onClick={() => setDetailOb(ob)} data-testid={`ob-card-${ob.employee_code}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-[#0B1B33]">{ob.employee_name}</p>
                      <span className="text-xs text-slate-400 font-mono">{ob.employee_code}</span>
                      <Badge className={`text-xs capitalize ${STATUS_STYLES[ob.status]}`}>{ob.status.replace('_', ' ')}</Badge>
                    </div>
                    <div className="mt-2">
                      <Progress value={ob.progress || 0} className="h-2" />
                      <p className="text-xs text-slate-500 mt-1">{ob.progress || 0}% complete</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
          {items.length === 0 && <p className="text-center text-slate-400 py-8">No onboarding flows yet</p>}
        </div>
      )}

      {/* Start Onboarding Dialog */}
      <Dialog open={startDialog} onOpenChange={setStartDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Start Employee Onboarding</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Select Employee</Label>
              <Select value={selectedEmp} onValueChange={setSelectedEmp}>
                <SelectTrigger className="mt-1" data-testid="select-employee-onboard">
                  <SelectValue placeholder="Choose employee..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleStart} disabled={starting} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="confirm-start-onboarding">
              {starting ? 'Starting...' : 'Start Onboarding'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailOb} onOpenChange={() => setDetailOb(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailOb?.employee_name} — Onboarding</DialogTitle>
          </DialogHeader>
          {detailOb && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-2">
                <Badge className={`${STATUS_STYLES[detailOb.status]} capitalize`}>{detailOb.status.replace('_', ' ')}</Badge>
                <span className="text-sm text-slate-500">{detailOb.progress}% complete</span>
              </div>
              <Progress value={detailOb.progress || 0} className="h-2" />

              <h4 className="text-sm font-semibold">Steps</h4>
              {detailOb.steps?.map(s => (
                <div key={s.step_id} className="flex items-center gap-3 py-2">
                  {s.status === 'completed' ? <CheckCircle className="w-5 h-5 text-green-500 shrink-0" /> : <Clock className="w-5 h-5 text-slate-300 shrink-0" />}
                  <div className="flex-1">
                    <p className="text-sm">{s.name}</p>
                    {s.notes && <p className="text-xs text-slate-500">{s.notes}</p>}
                  </div>
                  <Badge variant="outline" className={`text-xs ${STATUS_STYLES[s.status]}`}>{s.status}</Badge>
                </div>
              ))}

              <Separator />
              <h4 className="text-sm font-semibold">Documents</h4>
              {detailOb.documents?.map(d => (
                <div key={d.doc_id} className="flex items-center gap-3 py-2">
                  {d.status === 'verified' ? <FileCheck className="w-5 h-5 text-green-500 shrink-0" /> :
                   d.status === 'uploaded' ? <Upload className="w-5 h-5 text-blue-500 shrink-0" /> :
                   <AlertTriangle className="w-5 h-5 text-slate-300 shrink-0" />}
                  <div className="flex-1">
                    <p className="text-sm">{d.name} {d.mandatory && <span className="text-red-500">*</span>}</p>
                    {d.filename && <p className="text-xs text-blue-600 truncate">{d.filename}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-xs ${STATUS_STYLES[d.status]}`}>{d.status}</Badge>
                    {d.status === 'uploaded' && (isAdmin() || isHR()) && (
                      <Button size="sm" variant="outline" className="text-xs h-6" onClick={(e) => { e.stopPropagation(); handleVerifyDoc(detailOb.id, d.doc_id); }}>
                        Verify
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {detailOb.status === 'in_progress' && (isAdmin() || isHR()) && (
                <Button onClick={() => handleComplete(detailOb.id)} className="w-full bg-green-600 hover:bg-green-700 gap-2 mt-4" data-testid="complete-onboarding-btn">
                  <CheckCircle className="w-4 h-4" /> Mark Onboarding Complete
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Admin: Config ---
function OnboardingConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDocName, setNewDocName] = useState('');

  useEffect(() => {
    api.get('/api/onboarding/config').then(({ data }) => setConfig(data)).finally(() => setLoading(false));
  }, []);

  const addDoc = () => {
    if (!newDocName.trim()) return;
    const docs = [...(config.required_documents || [])];
    docs.push({ id: `custom_${Date.now()}`, name: newDocName.trim(), mandatory: false });
    setConfig({ ...config, required_documents: docs });
    setNewDocName('');
  };

  const removeDoc = (id) => {
    if (id === 'aadhar') { toast.error('Aadhar Card cannot be removed'); return; }
    setConfig({ ...config, required_documents: config.required_documents.filter(d => d.id !== id) });
  };

  const toggleMandatory = (id) => {
    if (id === 'aadhar') return;
    setConfig({
      ...config,
      required_documents: config.required_documents.map(d =>
        d.id === id ? { ...d, mandatory: !d.mandatory } : d
      )
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/onboarding/config', config);
      toast.success('Configuration saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (loading) return <p className="text-center text-slate-400 py-8">Loading...</p>;

  return (
    <div className="space-y-4">
      <Card className="border-slate-200">
        <CardHeader className="pb-3"><CardTitle className="text-base">Required Documents</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {config?.required_documents?.map(d => (
            <div key={d.id} className="flex items-center gap-3" data-testid={`config-doc-${d.id}`}>
              <div className="flex-1">
                <p className="text-sm">{d.name} {d.mandatory && <span className="text-red-500 text-xs">(mandatory)</span>}</p>
              </div>
              <Switch checked={d.mandatory} onCheckedChange={() => toggleMandatory(d.id)} disabled={d.id === 'aadhar'} />
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeDoc(d.id)} disabled={d.id === 'aadhar'}>
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <Input value={newDocName} onChange={e => setNewDocName(e.target.value)} placeholder="Add new document type..." className="text-sm" data-testid="new-doc-input" />
            <Button variant="outline" size="sm" onClick={addDoc} className="shrink-0 gap-1" data-testid="add-doc-btn">
              <Plus className="w-3 h-3" /> Add
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3"><CardTitle className="text-base">Onboarding Steps</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {config?.steps?.map((s, i) => (
            <div key={s.id} className="flex items-center gap-3 py-1">
              <span className="w-6 h-6 rounded-full bg-[#0D2850] text-white text-xs flex items-center justify-center shrink-0">{i + 1}</span>
              <p className="text-sm flex-1">{s.name}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="save-config-btn">
        {saving ? 'Saving...' : 'Save Configuration'}
      </Button>
    </div>
  );
}

// --- Employee View ---
function MyOnboarding() {
  const [ob, setOb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const fileInputRef = React.useRef(null);
  const pendingDocRef = React.useRef(null);

  useEffect(() => {
    api.get('/api/onboarding/my').then(({ data }) => setOb(data.status === 'none' ? null : data)).finally(() => setLoading(false));
  }, []);

  const handleCompleteStep = async (stepId) => {
    try {
      const { data } = await api.put(`/api/onboarding/${ob.id}/step`, { step_id: stepId, status: 'completed' });
      setOb(prev => ({
        ...prev,
        progress: data.progress,
        steps: prev.steps.map(s => s.step_id === stepId ? { ...s, status: 'completed', completed_at: new Date().toISOString() } : s)
      }));
      toast.success('Step completed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleUploadDoc = (docId) => {
    pendingDocRef.current = docId;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const docId = pendingDocRef.current;
    if (!file || !docId) return;
    e.target.value = '';
    setUploadingDoc(docId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_id', docId);
      const { data } = await api.post(`/api/onboarding/${ob.id}/document-upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setOb(prev => ({
        ...prev,
        progress: data.progress,
        documents: prev.documents.map(d => d.doc_id === docId ? { ...d, status: 'uploaded', filename: data.filename || file.name, uploaded_at: new Date().toISOString() } : d)
      }));
      toast.success(`${file.name} uploaded`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploadingDoc(null);
    pendingDocRef.current = null;
  };

  const handleAckPolicy = async () => {
    try {
      await api.post(`/api/onboarding/${ob.id}/acknowledge-policy`);
      setOb(prev => ({ ...prev, policy_acknowledged: true }));
      toast.success('Policy acknowledged');
    } catch { toast.error('Failed'); }
  };

  if (loading) return <p className="text-center text-slate-400 py-8">Loading...</p>;
  if (!ob) return (
    <div className="text-center py-12">
      <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-500">No onboarding in progress</p>
      <p className="text-xs text-slate-400 mt-1">Your HR team will initiate your onboarding when ready</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileSelected} data-testid="onboard-file-input" />
      <div className="text-center">
        <Badge className={`${STATUS_STYLES[ob.status]} capitalize px-3 py-1`}>{ob.status.replace('_', ' ')}</Badge>
        <Progress value={ob.progress || 0} className="h-2.5 mt-3" />
        <p className="text-sm text-slate-500 mt-1">{ob.progress}% complete</p>
      </div>

      {/* Steps */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Onboarding Steps</CardTitle></CardHeader>
        <CardContent className="space-y-1 p-3">
          {ob.steps?.map((s, i) => (
            <div key={s.step_id} className="flex items-center gap-3 py-2.5 px-2 rounded-lg hover:bg-slate-50" data-testid={`my-step-${s.step_id}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${s.status === 'completed' ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                {s.status === 'completed' ? <CheckCircle className="w-4 h-4" /> : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${s.status === 'completed' ? 'text-green-700 line-through' : ''}`}>{s.name}</p>
                <p className="text-xs text-slate-400">{s.description}</p>
              </div>
              {s.status === 'pending' && (
                <Button size="sm" variant="outline" className="text-xs h-7 shrink-0" onClick={() => handleCompleteStep(s.step_id)}>
                  Complete
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Documents */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Documents</CardTitle></CardHeader>
        <CardContent className="space-y-1 p-3">
          {ob.documents?.map(d => (
            <div key={d.doc_id} className="flex items-center gap-3 py-2.5 px-2 rounded-lg hover:bg-slate-50" data-testid={`my-doc-${d.doc_id}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${d.status === 'verified' ? 'bg-green-500 text-white' : d.status === 'uploaded' ? 'bg-blue-500 text-white' : 'bg-slate-200'}`}>
                {d.status === 'verified' ? <CheckCircle className="w-4 h-4" /> : d.status === 'uploaded' ? <Upload className="w-4 h-4" /> : <Upload className="w-4 h-4 text-slate-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm">{d.name} {d.mandatory && <span className="text-red-500">*</span>}</p>
                {d.filename && d.status !== 'pending' ? (
                  <p className="text-xs text-blue-600 truncate">{d.filename}</p>
                ) : (
                  <p className="text-xs text-slate-400 capitalize">{d.status}</p>
                )}
              </div>
              {d.status === 'pending' && (
                <Button size="sm" variant="outline" className="text-xs h-7 shrink-0 gap-1" onClick={() => handleUploadDoc(d.doc_id)} disabled={uploadingDoc === d.doc_id}>
                  <Upload className="w-3 h-3" /> {uploadingDoc === d.doc_id ? 'Uploading...' : 'Upload'}
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Policy */}
      {!ob.policy_acknowledged && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">Policy Acknowledgement Required</p>
                <p className="text-xs text-amber-600 mt-1">Please review and accept the company policies to proceed.</p>
                <Button size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700 text-xs" onClick={handleAckPolicy} data-testid="ack-policy-btn">
                  I Accept the Company Policies
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// --- Main Page ---
export default function OnboardingPage() {
  const canManage = isAdmin() || isHR();

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="onboarding-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0B1B33]">Onboarding</h1>
        <p className="text-sm text-slate-500 mt-1">{canManage ? 'Manage employee onboarding workflows' : 'Complete your onboarding steps'}</p>
      </div>

      {canManage ? (
        <Tabs defaultValue="tracker">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="tracker" data-testid="tab-tracker">Status Tracker</TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config">Configuration</TabsTrigger>
          </TabsList>
          <TabsContent value="tracker"><OnboardingTracker /></TabsContent>
          <TabsContent value="config"><OnboardingConfig /></TabsContent>
        </Tabs>
      ) : (
        <MyOnboarding />
      )}
    </div>
  );
}
